// src/types/asset.ts
// TypeScript interfaces for qmLEO Assets Module
// Created: 2025-01-29

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export type AssetStatus = 'active' | 'disposed';

export type VerificationStatus = 'verified' | 'pending' | 'overdue' | 'new';

export type AssetCondition = 'good' | 'fair' | 'poor' | 'damaged';

export type DisposalMethod = 
  | 'sold' 
  | 'donated' 
  | 'transferred' 
  | 'scrapped' 
  | 'stolen' 
  | 'trade_in';

export type FundingSource = 
  | 'general_fund' 
  | 'federal_grant' 
  | 'state_grant' 
  | 'donation' 
  | 'forfeiture' 
  | 'other';

// ============================================================================
// ASSET CATEGORY
// ============================================================================

export interface AssetCategory {
  // Keys
  tenantId: string;
  categoryId: string;
  
  // Category info
  name: string;
  description?: string;
  sortOrder: number;
  
  // Verification rules
  verificationFrequencyMonths: number;  // Default 12
  requiresPhotoOnVerification: boolean;
  
  // Status
  isActive: boolean;
  
  // Audit
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface CreateAssetCategoryRequest {
  name: string;
  description?: string;
  sortOrder?: number;
  verificationFrequencyMonths?: number;
  requiresPhotoOnVerification?: boolean;
  isActive?: boolean;
}

export interface UpdateAssetCategoryRequest {
  name?: string;
  description?: string;
  sortOrder?: number;
  verificationFrequencyMonths?: number;
  requiresPhotoOnVerification?: boolean;
  isActive?: boolean;
}

// ============================================================================
// TRACKED ASSET
// ============================================================================

export interface TrackedAsset {
  // Keys
  tenantId: string;
  assetId: string;
  
  // Core identification
  assetTag: string;              // Owner-applied number "WWPD-2024-0451"
  name: string;                  // "Forensic Workstation #3"
  description?: string;
  
  // Classification
  categoryId: string;
  categoryName: string;          // Denormalized for display
  
  // Location
  location: string;              // Free text "Evidence Lab - Room 204"
  
  // Financial
  purchaseDate?: string;
  purchaseValue?: number;
  currentValue?: number;
  fundingSource?: FundingSource;
  grantId?: string;              // For grant-funded items
  
  // Item details
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  photoUrl?: string;
  notes?: string;
  
  // Verification status (computed/cached)
  lastVerifiedAt?: string;
  lastVerifiedBy?: string;
  lastVerifiedByName?: string;
  verificationStatus: VerificationStatus;
  verificationDueDate?: string;
  
  // Lifecycle
  status: AssetStatus;
  
  // Disposal info (populated when disposed)
  disposedAt?: string;
  disposalMethod?: DisposalMethod;
  disposedTo?: string;           // Recipient name/org
  disposalAmount?: number;       // Sale price if sold
  disposalAuthorizedBy?: string;
  disposalReference?: string;    // PO number, authorization doc, etc.
  disposalNotes?: string;
  
  // Audit fields
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface CreateAssetRequest {
  assetTag: string;
  name: string;
  description?: string;
  categoryId: string;
  location: string;
  purchaseDate?: string;
  purchaseValue?: number;
  currentValue?: number;
  fundingSource?: FundingSource;
  grantId?: string;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  photoUrl?: string;
  notes?: string;
}

export interface UpdateAssetRequest {
  name?: string;
  description?: string;
  categoryId?: string;
  location?: string;
  purchaseDate?: string;
  purchaseValue?: number;
  currentValue?: number;
  fundingSource?: FundingSource;
  grantId?: string;
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  photoUrl?: string;
  notes?: string;
}

export interface DisposeAssetRequest {
  disposalMethod: DisposalMethod;
  disposedTo?: string;
  disposalAmount?: number;
  disposalAuthorizedBy: string;
  disposalReference?: string;
  disposalNotes?: string;
}

// ============================================================================
// ASSET VERIFICATION
// ============================================================================

export interface AssetVerification {
  // Keys
  tenantId: string;
  verificationId: string;
  
  // What was verified
  assetId: string;
  assetTag: string;
  assetName: string;
  
  // Verification details
  verifiedAt: string;
  verifiedBy: string;
  verifiedByName: string;
  
  // Location
  locationVerified: string;
  locationExpected?: string;
  locationMatch: boolean;
  
  // Condition
  condition?: AssetCondition;
  notes?: string;
  photoUrl?: string;
  
  // Cycle tracking (Phase 4)
  fiscalYear?: string;
  cycleId?: string;
}

export interface CreateVerificationRequest {
  assetId: string;
  locationVerified: string;
  condition?: AssetCondition;
  notes?: string;
  photoUrl?: string;
}

// ============================================================================
// API RESPONSES
// ============================================================================

export interface AssetAPIResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

export interface ListAssetsResponse {
  assets: TrackedAsset[];
  count: number;
  lastEvaluatedKey?: string;
}

export interface ListAssetCategoriesResponse {
  categories: AssetCategory[];
  count: number;
}

export interface ListVerificationsResponse {
  verifications: AssetVerification[];
  count: number;
  lastEvaluatedKey?: string;
}

// ============================================================================
// FILTERS & QUERIES
// ============================================================================

export interface AssetListFilters {
  status?: AssetStatus | 'all';
  categoryId?: string;
  verificationStatus?: VerificationStatus | 'all';
  location?: string;
  search?: string;
  limit?: number;
  lastEvaluatedKey?: string;
}

export interface VerificationListFilters {
  assetId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  lastEvaluatedKey?: string;
}

// ============================================================================
// MODULE CONFIGURATION
// ============================================================================

export interface AssetsModuleConfig {
  enabled: boolean;
  enabledAt?: string;
  enabledBy?: string;
  settings?: {
    defaultVerificationFrequencyMonths?: number;
    requirePhotoOnVerification?: boolean;
    allowAuditorToSeeDisposed?: boolean;
  };
}

// For tenant configuration integration
export interface TenantModules {
  inventory?: { enabled: boolean };
  assets?: AssetsModuleConfig;
}

// ============================================================================
// DEFAULT ASSET CATEGORIES
// ============================================================================

export const DEFAULT_ASSET_CATEGORIES: Omit<AssetCategory, 'tenantId' | 'categoryId' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'>[] = [
  { name: 'IT Equipment', description: 'Computers, servers, networking', sortOrder: 1, verificationFrequencyMonths: 12, requiresPhotoOnVerification: false, isActive: true },
  { name: 'Lab Equipment', description: 'Forensic, scientific instruments', sortOrder: 2, verificationFrequencyMonths: 12, requiresPhotoOnVerification: false, isActive: true },
  { name: 'Vehicles', description: 'Cars, trucks, specialty vehicles', sortOrder: 3, verificationFrequencyMonths: 12, requiresPhotoOnVerification: false, isActive: true },
  { name: 'Communications', description: 'Radios, satellite equipment', sortOrder: 4, verificationFrequencyMonths: 12, requiresPhotoOnVerification: false, isActive: true },
  { name: 'Optics & Imaging', description: 'Cameras, night vision, scopes', sortOrder: 5, verificationFrequencyMonths: 12, requiresPhotoOnVerification: false, isActive: true },
  { name: 'Specialized Tools', description: 'Technical equipment', sortOrder: 6, verificationFrequencyMonths: 12, requiresPhotoOnVerification: false, isActive: true },
  { name: 'Office Equipment', description: 'High-value office items', sortOrder: 7, verificationFrequencyMonths: 12, requiresPhotoOnVerification: false, isActive: true },
  { name: 'Other', description: 'Catch-all category', sortOrder: 99, verificationFrequencyMonths: 12, requiresPhotoOnVerification: false, isActive: true },
];

// ============================================================================
// FUNDING SOURCE LABELS (for UI dropdowns)
// ============================================================================

export const FUNDING_SOURCE_LABELS: Record<FundingSource, string> = {
  general_fund: 'General Fund',
  federal_grant: 'Federal Grant',
  state_grant: 'State Grant',
  donation: 'Donation',
  forfeiture: 'Asset Forfeiture',
  other: 'Other',
};

export const DISPOSAL_METHOD_LABELS: Record<DisposalMethod, string> = {
  sold: 'Sold',
  donated: 'Donated',
  transferred: 'Transferred to Another Agency',
  scrapped: 'Scrapped/Destroyed',
  stolen: 'Stolen/Missing',
  trade_in: 'Trade-in',
};

export const CONDITION_LABELS: Record<AssetCondition, string> = {
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
  damaged: 'Damaged',
};
